import {
  FbqCommand,
  FbqFunction,
  MetaPixelEvent,
  MetaPixelPayload,
  MetaPixelTransportConfig,
  Transport,
  WindowWithFbq,
} from '../meta-pixel.types';

const META_PIXEL_SCRIPT_ID = 'meta-pixel-base';
const META_PIXEL_SCRIPT_URL = 'https://connect.facebook.net/en_US/fbevents.js';

const createFbqStub = (): FbqFunction => {
  const stub = ((...args: FbqCommand) => {
    if (stub.callMethod) {
      stub.callMethod(...args);
      return;
    }
    if (!stub.queue) {
      stub.queue = [];
    }
    stub.queue.push(args);
  }) as FbqFunction;

  stub.queue = [];
  stub.loaded = true;
  stub.version = '2.0';
  stub.push = stub;

  return stub;
};

export const createMetaPixelTransport = (): Transport => {
  let _initialized = false;
  let _initializing = false;
  let _initializationFailed = false;

  const getFbq = (): FbqFunction | null => {
    if (_initializationFailed) return null;
    const win = window as WindowWithFbq;
    if (!win.fbq) {
      console.warn('Meta Pixel not initialized');
      return null;
    }
    return win.fbq;
  };

  return {
    name: 'metapixel',

    init(config: MetaPixelTransportConfig): void {
      if (_initialized || _initializing || _initializationFailed) return;
      if (!config.pixelId) {
        console.warn(
          'Meta Pixel: No Pixel ID provided, skipping initialization',
        );
        return;
      }

      try {
        const win = window as WindowWithFbq;
        const fbq = win.fbq ?? createFbqStub();
        win.fbq = fbq;
        if (!win._fbq) win._fbq = fbq;

        fbq('init', config.pixelId);
        fbq('track', 'PageView');

        const existingScript = document.getElementById(
          META_PIXEL_SCRIPT_ID,
        ) as HTMLScriptElement | null;
        if (existingScript) {
          _initialized = true;
          return;
        }

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.id = META_PIXEL_SCRIPT_ID;
        script.src = META_PIXEL_SCRIPT_URL;

        script.onload = () => {
          _initialized = true;
          _initializing = false;
          console.info('Meta Pixel initialized');
        };

        script.onerror = () => {
          _initializationFailed = true;
          _initializing = false;
          console.error('Meta Pixel failed to load');
        };

        const firstScript = document.getElementsByTagName('script')[0];
        _initializing = true;
        firstScript.parentNode?.insertBefore(script, firstScript);
      } catch (error) {
        _initializationFailed = true;
        _initializing = false;
        console.error('Meta Pixel initialization error:', error);
      }
    },

    track(eventName: MetaPixelEvent, payload?: MetaPixelPayload): void {
      const fbq = getFbq();
      if (!fbq) return;

      try {
        if (payload && Object.keys(payload).length > 0) {
          fbq('track', eventName, payload);
          return;
        }
        fbq('track', eventName);
      } catch (error) {
        console.error('Meta Pixel track error:', error);
      }
    },
  };
};
